import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  LayoutDashboard, FolderKanban, CheckSquare, Users, Calendar,
  Clock, Bell, LogOut, Menu, X, Camera, ChevronDown, User, UserCog, Briefcase, Target, BarChart3, Database, IndianRupee, CalendarDays, HardDrive, Shield, Send, BellRing, ScanFace
} from 'lucide-react';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'sales_person', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { path: '/projects', label: 'Projects', icon: FolderKanban, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'freelancer'] },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media'] },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { path: '/data-management', label: 'Data Management', icon: Database, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer'] },
  { path: '/my-shoots', label: 'My Shoots', icon: HardDrive, roles: ['editor_shooter', 'photographer', 'videographer'] },
  { path: '/accounts', label: 'Accounts', icon: IndianRupee, roles: ['admin', 'manager'] },
  { path: '/team', label: 'Team', icon: Users, roles: ['admin', 'manager'] },
  { path: '/attendance', label: 'Attendance', icon: Calendar, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'sales_person'] },
  { path: '/time-tracking', label: 'Time Tracking', icon: Clock, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { path: '/freelancers', label: 'Freelancers', icon: UserCog, roles: ['admin', 'manager'] },
  { path: '/freelancer-portal', label: 'My Projects', icon: Briefcase, roles: ['freelancer'] },
  { path: '/leads', label: 'Leads & Sales', icon: Target, roles: ['admin', 'manager', 'sales_person'] },
  { path: '/sales-reports', label: 'Sales Reports', icon: BarChart3, roles: ['admin', 'manager', 'sales_person'] },
  { path: '/transfers', label: 'Transfers', icon: Send, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media', 'freelancer'] },
  { path: '/face-search', label: 'Face Search', icon: ScanFace, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media', 'freelancer'] },
  { path: '/reminders', label: 'Reminders', icon: BellRing, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media', 'freelancer'] },
  { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'manager'] },
  { path: '/admin', label: 'Admin Panel', icon: Shield, roles: ['admin'] },
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?unread_only=true');
      setNotifications(res.data.slice(0, 5));
      setUnreadCount(res.data.length);
    } catch (err) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setUnreadCount(0);
      setNotifications([]);
    } catch (err) {
      console.error('Failed to mark notifications as read');
    }
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => {
    const userRoles = user?.roles || [user?.role];
    return userRoles.some(r => item.roles.includes(r));
  });

  const getNotificationIcon = (type) => {
    const colors = { warning: 'bg-amber-500', info: 'bg-blue-500', success: 'bg-emerald-500', error: 'bg-rose-500' };
    return colors[type] || colors.info;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-[#0A0A0A] border-r border-white/10">
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <div className="p-2 bg-[#F5A623] rounded">
            <Camera className="h-5 w-5 text-black" />
          </div>
          <div>
            <h1 className="text-white font-semibold tracking-tight">Wittyshadows</h1>
            <p className="text-xs text-zinc-500">Studio Ops</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNavItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded transition-colors ${
                  isActive 
                    ? 'bg-[#F5A623]/10 text-[#F5A623] border-l-2 border-[#F5A623]' 
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info + Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
              <User className="h-4 w-4 text-[#F5A623]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 capitalize">{(user?.roles || [user?.role]).map(r => r?.replace(/_/g, ' ')).join(', ')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-[#0A0A0A] border-r border-white/10">
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F5A623] rounded">
                  <Camera className="h-5 w-5 text-black" />
                </div>
                <span className="text-white font-semibold">Wittyshadows</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="px-4 py-6 space-y-1">
              {filteredNavItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded transition-colors ${
                      isActive 
                        ? 'bg-[#F5A623]/10 text-[#F5A623]' 
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            {/* Mobile Logout */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
              <div className="flex items-center gap-3 px-2 mb-3">
                <div className="h-8 w-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                  <User className="h-4 w-4 text-[#F5A623]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-500 capitalize">{(user?.roles || [user?.role]).map(r => r?.replace(/_/g, ' ')).join(', ')}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Top Header */}
        <header className="h-16 border-b border-white/10 bg-[#0A0A0A]/70 backdrop-blur-xl sticky top-0 z-40">
          <div className="h-full flex items-center justify-between px-4 lg:px-8">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}
                  className="relative p-2 text-zinc-400 hover:text-white rounded-md hover:bg-white/5 transition-colors"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-rose-500 rounded-full flex items-center justify-center text-xs text-white font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-[#121212] border border-white/10 rounded-lg shadow-2xl z-50">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
                      <span className="text-white font-medium text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <button onClick={handleMarkAllRead} className="text-xs text-zinc-400 hover:text-white">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <ScrollArea className="max-h-80">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-zinc-500 text-sm">
                          No new notifications
                        </div>
                      ) : (
                        notifications.map(notif => (
                          <div key={notif.id} className="px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0 cursor-pointer"
                            onClick={() => { if (notif.link) { window.location.href = notif.link; setShowNotifications(false); } }}>
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 mt-1.5 rounded-full shrink-0 ${getNotificationIcon(notif.type)}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white font-medium">{notif.title}</p>
                                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{notif.message}</p>
                                <p className="text-[10px] text-zinc-600 mt-1">{notif.created_at ? new Date(notif.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </ScrollArea>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}
                  className="flex items-center gap-2 px-2 py-1.5 text-zinc-400 hover:text-white rounded-md hover:bg-white/5 transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="h-8 w-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                    <User className="h-4 w-4 text-[#F5A623]" />
                  </div>
                  <span className="hidden sm:inline text-sm">{user?.name?.split(' ')[0]}</span>
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#121212] border border-white/10 rounded-lg shadow-2xl z-50">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-white font-medium">{user?.name}</p>
                      <p className="text-xs text-zinc-500">{user?.email}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(user?.roles || [user?.role]).map(r => (
                          <Badge key={r} variant="outline" className="text-xs bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20 capitalize">
                            {r?.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                      data-testid="logout-btn"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 min-w-0 max-w-full">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
